// Calendar integration utilities for ShowingBooker
// Supports Google Calendar, Outlook, Apple Calendar, and ICS download

class CalendarUtils {
  
  // Format date for calendar APIs (YYYYMMDDTHHMMSSZ)
  formatDateForCalendar(date, time) {
    const showingDate = new Date(`${date}T${time}:00`);
    return showingDate.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  }

  // Calculate end time (30 minutes after start)
  getEndTime(date, time) {
    const showingDate = new Date(`${date}T${time}:00`);
    const endDate = new Date(showingDate.getTime() + 30 * 60000); // Add 30 minutes
    return endDate.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  }

  // Generate event details
  generateEventDetails(booking, property) {
    const address = `${property.address_line1}${property.unit_number ? ', ' + property.unit_number : ''}, ${property.city}, ${property.state} ${property.zip_code}`;
    
    const title = `Property Showing - ${property.address_line1}`;
    
    const description = [
      `Scheduled property showing`,
      ``,
      `Property: ${address}`,
      `Confirmation: ${booking.confirmation_number}`,
      ``,
      `Buyer: ${booking.buyerName || booking.buyer_name}`,
      (booking.buyerAgentName || booking.buyer_agent_name) && (booking.buyerAgentName || booking.buyer_agent_name) !== 'Not provided' ? `Buyer's Agent: ${booking.buyerAgentName || booking.buyer_agent_name}` : '',
      ``,
      `Listing Agent: ${property.agent_name}`,
      `Agent Phone: ${property.agent_phone}`,
      ``,
      property.showing_instructions ? `Instructions: ${property.showing_instructions}` : '',
      ``,
      `Duration: 30 minutes`,
      `Please arrive on time and have ID ready.`,
      ``,
      `Powered by ShowingBooker`
    ].filter(line => line !== null && line !== '').join('\\n');

    // Handle both camelCase and snake_case field names for compatibility
    const showingDate = booking.showingDate || booking.showing_date;
    const showingTime = booking.showingTime || booking.showing_time;
    
    const startTime = this.formatDateForCalendar(showingDate, showingTime);
    const endTime = this.getEndTime(showingDate, showingTime);

    return {
      title,
      description,
      location: address,
      startTime,
      endTime
    };
  }

  // Generate Google Calendar URL
  generateGoogleCalendarUrl(booking, property) {
    const event = this.generateEventDetails(booking, property);
    
    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: event.title,
      dates: `${event.startTime}/${event.endTime}`,
      details: event.description,
      location: event.location,
      ctz: Intl.DateTimeFormat().resolvedOptions().timeZone
    });

    return `https://calendar.google.com/calendar/render?${params.toString()}`;
  }

  // Generate Outlook Calendar URL
  generateOutlookCalendarUrl(booking, property) {
    const event = this.generateEventDetails(booking, property);
    
    const params = new URLSearchParams({
      path: '/calendar/action/compose',
      rru: 'addevent',
      subject: event.title,
      startdt: event.startTime,
      enddt: event.endTime,
      body: event.description,
      location: event.location
    });

    return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
  }

  // Generate ICS file content (for Apple Calendar and download)
  generateICSContent(booking, property) {
    const event = this.generateEventDetails(booking, property);
    const now = new Date().toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    const uid = `showing-${booking.confirmation_number}@showingbooker.com`;

    return [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//ShowingBooker//Property Showing//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      'BEGIN:VEVENT',
      `UID:${uid}`,
      `DTSTAMP:${now}`,
      `DTSTART:${event.startTime}`,
      `DTEND:${event.endTime}`,
      `SUMMARY:${event.title}`,
      `DESCRIPTION:${event.description}`,
      `LOCATION:${event.location}`,
      'STATUS:CONFIRMED',
      'TRANSP:OPAQUE',
      'BEGIN:VALARM',
      'TRIGGER:-PT15M',
      'ACTION:DISPLAY',
      'DESCRIPTION:Property showing reminder',
      'END:VALARM',
      'END:VEVENT',
      'END:VCALENDAR'
    ].join('\\r\\n');
  }

  // Download ICS file
  downloadICSFile(booking, property) {
    const icsContent = this.generateICSContent(booking, property);
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `showing-${booking.confirmation_number}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  // Open calendar in new tab
  openCalendar(type, booking, property) {
    let url;
    
    switch (type) {
      case 'google':
        url = this.generateGoogleCalendarUrl(booking, property);
        break;
      case 'outlook':
        url = this.generateOutlookCalendarUrl(booking, property);
        break;
      case 'apple':
      case 'download':
        this.downloadICSFile(booking, property);
        return;
      default:
        console.error('Unknown calendar type:', type);
        return;
    }
    
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  // Get user's likely calendar preference based on user agent
  detectPreferredCalendar() {
    const userAgent = navigator.userAgent.toLowerCase();
    
    if (userAgent.includes('mac') || userAgent.includes('iphone') || userAgent.includes('ipad')) {
      return 'apple';
    } else if (userAgent.includes('android')) {
      return 'google';
    } else if (userAgent.includes('windows')) {
      return 'outlook';
    } else {
      return 'google'; // Default fallback
    }
  }

  // Generate all calendar options
  getAllCalendarOptions(booking, property) {
    return [
      {
        type: 'google',
        name: 'Google Calendar',
        icon: '📅',
        action: () => this.openCalendar('google', booking, property)
      },
      {
        type: 'outlook',
        name: 'Outlook Calendar',
        icon: '📆',
        action: () => this.openCalendar('outlook', booking, property)
      },
      {
        type: 'apple',
        name: 'Apple Calendar',
        icon: '📅',
        action: () => this.openCalendar('apple', booking, property)
      },
      {
        type: 'download',
        name: 'Download .ics',
        icon: '💾',
        action: () => this.openCalendar('download', booking, property)
      }
    ];
  }
}

export default new CalendarUtils();